/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import equal from 'fast-deep-equal';
import React, { ChangeEvent, memo, ReactElement, useEffect, useMemo, useState } from 'react';
import isFunction from 'lodash/isFunction';
import isString from 'lodash/isString';

import { calcInputSelection, getInputMaxLength } from 'src/components/UI/inputs/common';
import { UiMereLabel } from 'src/components/UI/UiMereLabel';
import { getDOMElement } from 'src/utils/DOM';
import { useIdOrRandomString } from 'src/utils/formHelper';
import { UiHoverTooltip } from 'src/components/UI/HoverTooltip';
import { useCleanHoverErrors } from 'src/components/UI/inputs/useHoverErrors';
import { StateFormInputOptionsType, StateFormReturnType } from 'src/utils/stateForm';
import { getTestId, TestIdAddType } from 'src/utils/testing';
import { useInputCleanBaseHook } from 'src/utils/hooks/inputs';
import { tryCatch } from 'src/utils/tryCatch';
import { InputMaterialWrapper } from 'src/components/UI/inputs/common/InputMaterialWrapper';
import { MaterialLabel } from 'src/components/UI/common/MaterialLabel';

export type UiCleanInputProps = {
  formProps: StateFormReturnType;
  name: string;

  required?: boolean;
  pattern?: RegExp; // the regex pattern for the input (blocks an input, allows only input symbols from regExp)
  placeholder?: string | React.ReactNode;
  id?: string;
  showAmount?: boolean; // showing by default if maxLength is set
  disabled?: boolean;
  replaceValue?: (v: string) => string; // the function to replace input value
  firstCharEachWordUpper?: boolean; // test jest -> Test Jest
  requiredMessage?: string;
  errorLabel?: string;
  type?: 'email' | 'text' | 'password' | 'textarea'; // for type="number" use UiCleanFormatNumberInput
  maxLength?: number;
  minLength?: number;
  onBlur?: (value: string | null | undefined) => void;
  // the function to validate value (not blocking an input) may override required
  validate?: StateFormInputOptionsType['validate'];
  className?: string;
  classNameLabel?: string;
  prefix?: string | ReactElement; // input prefix
  customErrorMessage?: string;
  autoFocus?: boolean; // set focus on mount
  label?: string | React.ReactNode;
  noParagraph?: boolean;
  defaultValue?: string;
  rows?: number;
  noMaterialize?: boolean;
  dataTestId?: string;
  showHoverError?: boolean;
  validateName?: string; // for api request errors. Use it when you need to pass only the name field without nesting
  onChange?: (value: string | null) => void;
  material?: boolean; // new field-material design
  hideMaterialErrors?: boolean;
  wrapperClassName?: string;
  onEnter?: () => void;
  calculatePadding?: boolean;
  defaultPlaceholderStyle?: boolean;
  classNameInputGroup?: string;
  inputMode?: 'numeric';
};

export const UiCleanInput: React.FC<UiCleanInputProps> = memo(
  ({
    name,
    formProps: { clearErrors, onChange, onBlur: formOnBlur, register, unregister, getSubscribeProps, setRef },
    maxLength,
    required,
    requiredMessage,
    minLength,
    validate,
    placeholder,
    id: propId,
    type = 'text',
    showAmount = true,
    firstCharEachWordUpper = false,
    prefix,
    pattern,
    replaceValue,
    className,
    disabled,
    customErrorMessage,
    autoFocus,
    label,
    errorLabel,
    classNameLabel,
    noParagraph,
    rows,
    noMaterialize,
    showHoverError = false,
    dataTestId,
    onBlur,
    validateName,
    onChange: handlerOnChange,
    material,
    hideMaterialErrors,
    wrapperClassName,
    onEnter,
    calculatePadding,
    defaultPlaceholderStyle,
    defaultValue,
    classNameInputGroup,
    inputMode,
  }) => {
    const id = useIdOrRandomString(propId);

    const [active, setActive] = useState(false);

    const localMaxLength = useMemo(() => getInputMaxLength(maxLength), [maxLength]);

    const countCharsAmount = localMaxLength && showAmount && active;

    const isMaterializeTextarea = type === 'textarea' && !noMaterialize && !material;

    const inputOptions = useMemo(
      (): StateFormInputOptionsType => ({
        required: required || false,
        minLength,
        requiredMessage,
        validate,
        errorLabel: errorLabel || (isString(placeholder) ? placeholder : false) || undefined,
        maxLength,
      }),
      [errorLabel, maxLength, minLength, placeholder, required, requiredMessage, validate]
    );

    const [value, errors] = useInputCleanBaseHook<string | number | null | undefined>({
      getSubscribeProps,
      name,
      inputOptions,
      unregister,
      register,
      type,
      validateName,
    });
    const hasErrors = !!errors?.length;

    /** initialization effects */
    useEffect(() => {
      if (isMaterializeTextarea) {
        tryCatch(() => {
          // a hack for resizing textarea on init
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          $('.materialize-textarea').keydown();
        });
      }
    }, [isMaterializeTextarea]);

    useEffect(() => {
      if (autoFocus) {
        getDOMElement(`#${id}`)?.focus();
      }
      // eslint-disable-next-line
    }, []);
    /** end initialization effects */

    const { errorString, onChangeInputHover } = useCleanHoverErrors({
      name,
      validateName,
      errors,
      clearErrors,
      showAnyError: showHoverError || (material && !hideMaterialErrors),
    });

    const baseTestId: TestIdAddType = useMemo(
      () =>
        dataTestId
          ? getTestId(type === 'textarea' ? 'input_textarea' : 'input_text', dataTestId, name)
          : () => undefined,
      [dataTestId, name, type]
    );

    const [paddingLeft, setPaddingLeft] = useState<number | undefined>();

    /** common props */
    const props = {
      id,
      style: { paddingLeft },
      className: classNames(className, prefix && isString(prefix) ? `mp-${prefix.length}` : undefined, {
        required: hasErrors && required,
        error: hasErrors,
        'no-resize': !isMaterializeTextarea,
        'materialize-textarea': isMaterializeTextarea,
      }),
      disabled,
      'data-name': name,
      maxLength: localMaxLength,
      placeholder: (material && !defaultPlaceholderStyle) || !isString(placeholder) ? undefined : placeholder,
      readOnly: disabled,
      onKeyPress: ({ preventDefault, key }: React.KeyboardEvent) => {
        if (disabled) {
          preventDefault();
        } else if (key === 'Enter' && isFunction(onEnter)) {
          onEnter();
        }
      },
      onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (disabled) {
          return;
        }

        onChangeInputHover();

        const {
          target: { value, selectionStart },
        } = e;

        /** handle it all manually */
        let tempValue: string | null = value;

        if (firstCharEachWordUpper) {
          tempValue = tempValue
            .split(' ')
            .map((str) => {
              if (str[0]) {
                const [firstChar, ...otherChars] = str;
                return `${firstChar.toUpperCase()}${otherChars.join('')}`;
              }
              return str;
            })
            .join(' ');
        }

        tempValue = tempValue.trimStart(); // string mustn't start with spaces

        /** the second check of maxLength in case of pasting a value (and also for tests) */
        if (localMaxLength) {
          if (tempValue.length >= localMaxLength) {
            tempValue = tempValue.substring(0, localMaxLength);
          }
        }

        if (tempValue.length && pattern) {
          if (!pattern.test(tempValue)) {
            return;
          }
        }

        /** empty string -> null */
        if (tempValue.length === 0) {
          tempValue = null;
        } else {
          /** remove all spaces(! not \s because of \n) in a row but one */
          tempValue = tempValue.replace(/ +/g, ' ');
        }

        if (isFunction(replaceValue) && tempValue) {
          tempValue = replaceValue(tempValue);
        }

        onChange(name, tempValue);
        if (isFunction(handlerOnChange)) {
          handlerOnChange(tempValue);
        }

        if (selectionStart) {
          // safari https://www.py4u.net/discuss/279152
          e.target.selectionEnd = calcInputSelection(selectionStart, value);
        }
      },
      onFocus: () => {
        if (!disabled) {
          setActive(true);
        }
      },
      onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (!disabled) {
          setActive(false);
        }

        formOnBlur(name);

        if (isFunction(onBlur)) {
          onBlur(e.target.value);
        }
      },
      ref: setRef(name),
    };

    const InputComponent =
      type === 'textarea' ? (
        // <textarea className="materialize-textarea" />
        <textarea
          {...props}
          spellCheck="false"
          value={value || ''}
          rows={rows}
          data-testid={baseTestId()}
          defaultValue={defaultValue}
        />
      ) : (
        <input
          type={type}
          {...props}
          value={value || ''}
          data-testid={baseTestId()}
          defaultValue={defaultValue}
          inputMode={inputMode}
        />
      );

    if (material) {
      return (
        <>
          {label && <MaterialLabel id={id}>{label}</MaterialLabel>}
          <InputMaterialWrapper
            active={active || !!prefix}
            disabled={disabled}
            hasErrors={hasErrors}
            errorMessage={customErrorMessage || errorString}
            placeholder={defaultPlaceholderStyle ? undefined : placeholder}
            hasValue={!!value}
            className={wrapperClassName}
          >
            {prefix && (
              <span
                className="prefix"
                data-testid={baseTestId('prefix')}
                ref={(element) => {
                  if (calculatePadding && element && paddingLeft === undefined) {
                    const prefixWidth = parseInt(window.getComputedStyle(element).getPropertyValue('width'), 10);

                    setPaddingLeft(prefixWidth + 1);
                  }
                }}
              >
                {prefix}
              </span>
            )}
            {InputComponent}
          </InputMaterialWrapper>
        </>
      );
    }

    const Component = (
      <>
        <UiHoverTooltip id={name} text={errorString} render={!!errorString} tooltipClassName="error-tooltip" noWrapper>
          {InputComponent}
        </UiHoverTooltip>

        {customErrorMessage && (
          <span className="fs-12 input-sub-error" data-testid={baseTestId('customerrormessage')}>
            {customErrorMessage}
          </span>
        )}

        {countCharsAmount && (
          <span className="character-counter fs-12" data-testid={baseTestId('charcounter')}>
            {value?.toString().length || 0}/{localMaxLength}
          </span>
        )}
      </>
    );

    const InputLabelWrapper = noParagraph ? React.Fragment : 'p';

    return prefix ? (
      <div className={classNames('input-group', classNameInputGroup)}>
        <span className="prefix" data-testid={baseTestId('prefix')}>
          {prefix}
        </span>
        {Component}
      </div>
    ) : (
      <>
        {label ? (
          <InputLabelWrapper>
            <UiMereLabel className={classNameLabel}>{label}</UiMereLabel>
            {Component}
          </InputLabelWrapper>
        ) : (
          Component
        )}
      </>
    );
  },
  equal
);
